import React, { useEffect, useState } from "react";
import { Row, Col, Card, Button } from "react-bootstrap";
import { FaEnvelope, FaMicrosoft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useLocalStorageState from "use-local-storage-state";
import {
  checkOutlookStatus,
  connectOutlook,
} from "../../Calendar/outlookClient";

export default function EmailSettings() {
  const navigate = useNavigate();
  const [customer] = useLocalStorageState("customer");
  const GMAIL_CLIENT_ID =
    "791656083371-20fe92r5aq46otpm37miktc966919gfv.apps.googleusercontent.com";
  const GMAIL_REDIRECT_URI = `${window.origin}/email-settings`;
  const GMAIL_SCOPE =
    "email profile https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/gmail.labels https://www.googleapis.com/auth/gmail.send";

  const gmailLoginUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GMAIL_CLIENT_ID}&redirect_uri=${encodeURIComponent(
    GMAIL_REDIRECT_URI,
  )}&scope=${encodeURIComponent(
    GMAIL_SCOPE,
  )}&response_type=token&prompt=consent`;

  const [token, setToken] = useState(
    sessionStorage.getItem("email_access_token"),
  );
  const [service, setService] = useState(
    sessionStorage.getItem("email_service"),
  );

  const [isOutlookLoggedIn, setIsOutlookLoggedIn] = useState(
    () => localStorage.getItem("outlook_connected") === "true",
  );

  const handleGmailLogin = () => {
    sessionStorage.clear();
    window.location.href = gmailLoginUrl;
  };

  const handleOutlookLogin = () => {
    const userId = customer?.customer_details?.id;
    if (!userId) {
      toast.error("Can't connect Outlook - user not identified.");
      return;
    }
    connectOutlook(userId);
  };

  const handleDisconnect = () => {
    sessionStorage.clear();
    localStorage.removeItem("outlook_connected");
    setToken(null);
    setService(null);
    setIsOutlookLoggedIn(false);
    toast.success("Disconnected");
  };

  async function fetchGoogleUserInfo(accessToken) {
    try {
      const res = await fetch("https://www.googleapis.com/oauth2/v2/userinfo", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        sessionStorage.setItem("user_email", data.email);
        sessionStorage.setItem("user_name", data.name);
        return data;
      }
    } catch (error) {
      console.error("Failed to fetch Google user info:", error);
    }
    return null;
  }

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes("access_token")) {
      const p = new URLSearchParams(hash.substring(1));
      const accessToken = p.get("access_token");
      const expiresIn = p.get("expires_in");

      if (accessToken && expiresIn) {
        sessionStorage.setItem("email_access_token", accessToken);
        sessionStorage.setItem("email_service", "gmail");
        sessionStorage.setItem(
          "email_token_expiry",
          Date.now() + Number(expiresIn) * 1000,
        );

        fetchGoogleUserInfo(accessToken).then((userInfo) => {
          setToken(accessToken);
          setService("gmail");

          toast.success(
            userInfo?.email
              ? `Connected as ${userInfo.email}`
              : "Gmail connected",
          );

          window.history.replaceState({}, document.title, "/email-settings");
          navigate("/inbox", { replace: true });
        });
      }
    }
  }, [navigate]);
  useEffect(() => {
    const userId = customer?.customer_details?.id;
    if (!userId) return;

    checkOutlookStatus(userId).then((status) => {
      setIsOutlookLoggedIn(status?.connected);
      if (status?.connected) {
        localStorage.setItem("outlook_connected", "true");
        sessionStorage.setItem("email_service", "outlook");
        sessionStorage.setItem("outlook_email", status?.email);
        sessionStorage.setItem("outlook_name", status?.name);
        setService("outlook");
        toast.success("Outlook connected");
        navigate("/inbox", { replace: true });
      } else {
        localStorage.removeItem("outlook_connected");
        if (sessionStorage.getItem("email_service") === "outlook") {
          sessionStorage.removeItem("outlook_email");
          sessionStorage.removeItem("outlook_name");
          sessionStorage.removeItem("email_service");
        }
      }
    });
  }, [customer?.customer_details?.id, navigate]);

  // Gmail token comes back in the URL hash — only Gmail logic here now.
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes("access_token")) {
      const p = new URLSearchParams(hash.substring(1));
      const accessToken = p.get("access_token");
      const expiresIn = p.get("expires_in");

      if (accessToken && expiresIn) {
        sessionStorage.setItem("email_access_token", accessToken);
        sessionStorage.setItem("email_service", "gmail");
        sessionStorage.setItem(
          "email_token_expiry",
          Date.now() + Number(expiresIn) * 1000,
        );

        setToken(accessToken);
        setService("gmail");

        toast.success("Gmail connected");
        window.history.replaceState({}, document.title, "/email-settings");
        navigate("/inbox", { replace: true });
      }
    }
  }, [navigate]);

  const isConnected = !!token || isOutlookLoggedIn;

  return (
    <div
      className="p-4"
      style={{ backgroundColor: "#f5f6fa", minHeight: "100vh" }}
    >
      <Row className="mb-3">
        <Col>
          <h5>COMMUNICATION / EMAIL SETTINGS</h5>
        </Col>
        <Col className="text-end">
          <Button variant="secondary" onClick={() => navigate("/inbox")}>
            Back to Emails
          </Button>
        </Col>
      </Row>

      <Card className="p-4">
        {!isConnected ? (
          <Row className="text-center">
            <Col>
              <div
                onClick={handleGmailLogin}
                className="border border-1 br-10 p-3 clickable d-flex align-items-start cursor-pointer"
              >
                <div className="flex-shrink-0">
                  <FaEnvelope size={40} color="#d93025" />
                </div>
                <div className="ms-3 d-flex flex-column text-start">
                  <h6>Gmail</h6>
                  <span
                    className="text-muted"
                    style={{ fontSize: "14px", lineHeight: "1.4" }}
                  >
                    Send and receive emails directly in PugCRM using your Gmail
                    account. Access labels, manage threads, and stay connected
                    with clients — all without leaving the CRM.
                  </span>
                </div>
              </div>
            </Col>
            <Col>
              <div
                onClick={handleOutlookLogin}
                className="border border-1 br-10 p-3 clickable d-flex align-items-start cursor-pointer"
              >
                <div className="flex-shrink-0">
                  <FaMicrosoft size={40} color="#0078d4" />
                </div>
                <div className="ms-3 d-flex flex-column text-start">
                  <h6 className="fw-400 mb-1 mt-1">Outlook</h6>
                  <span
                    className="text-muted"
                    style={{ fontSize: "14px", lineHeight: "1.4" }}
                  >
                    Link your Outlook account to PugCRM to manage Microsoft
                    emails seamlessly. Send, receive, and track client
                    conversations directly from your CRM dashboard.
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        ) : (
          <>
            <p className="text-success">
              {service === "gmail" ? "Gmail" : "Outlook"} connected
            </p>
            <Button variant="danger" onClick={handleDisconnect}>
              Disconnect
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
